<template>
  <v-container fluid>
    <v-card>
      <v-toolbar color="secondary" dense flat>
        <v-toolbar-title>Transfer One Product to Multiple Stores</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn small text to="/transfer-drafts">
          <v-icon left dark>mdi-keyboard-backspace</v-icon>Back
        </v-btn>
      </v-toolbar>
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <div style="font-weight: bold; font-size: 20px; margin-top: 10px">
              From:
              <span class="custom-accent">{{ fromStore && fromStore.Name }}</span>
            </div>
          </v-col>
        </v-row>

        <v-row dense class="subtitle-2 text--primary">
          <!-- Product Selection Section -->
          <v-col cols="12" md="8">
            <v-autocomplete
              autocomplete="off"
              v-model="selectedProduct"
              :loading="isLoading"
              :items="products"
              :search-input.sync="searchProducts"
              @change="fetchProductInventory(selectedProduct)"
              :item-text="smsRef == 'pharmacy' ? 'full_name' : 'Name'"
              item-value="id"
              cache-items
              flat
              hide-no-data
              label="Search Product"
              outlined
              dense
              :disabled="!fromStore.id"
            >
              <template v-slot:prepend-inner>
                <v-icon>mdi-magnify</v-icon>
              </template>
            </v-autocomplete>
            <div v-if="!fromStore.id" class="caption error--text ml-2">
              Loading source store...
            </div>
          </v-col>

          <v-col cols="12" xs="12" sm="4" md="4">
            <span v-if="dateReadonly == 'Y' && role != 3" style="font-weight: bold; font-size: 16px">
              Date: {{ date }}
            </span>
            <DatePicker v-else @date="setDate" :myDate="date" :title="`Date`"></DatePicker>
          </v-col>

          <!-- Product Info Display -->
          <v-col cols="12" v-if="selectedProductInfo.ProductName">
            <v-card outlined class="pa-3">
              <div class="subtitle-1 font-weight-bold mb-2">
                {{ selectedProductInfo.ProductName }}
              </div>
              <div class="caption">
                <strong>Available in Source:</strong> <span style="color: red; font-weight: bold;">{{ selectedProductInfo.AvailableQty }}</span>
              </div>
            </v-card>
          </v-col>

          <!-- UOM Selection (if applicable) -->
          <v-col cols="12" md="6" v-if="selectedProductInfo.ProductName && selectedProductInfo.ManualStatus == 'N'">
            <v-autocomplete
              autocomplete="off"
              dense
              outlined
              single-line
              v-model="selectedProductInfo.UomEntry"
              :items="selectedProductInfo.ugp1"
              label="Unit of Measure"
              item-text="UomName"
              item-value="UomId"
              @change="uomChanged"
            ></v-autocomplete>
          </v-col>

          <!-- Stores List with Transfer Quantities -->
          <v-col cols="12" v-if="selectedProductInfo.ProductName">
            <v-card outlined>
              <v-card-title class="subtitle-2">Select Stores and Enter Transfer Quantities</v-card-title>
              <v-card-text>
                <v-data-table
                  :mobile-breakpoint="0"
                  light
                  dense
                  :items-per-page="pageItems"
                  :headers="storeHeaders"
                  :items="storesList"
                  item-key="id"
                  hide-default-footer
                  disable-sort
                >
                  <template v-slot:item.index="{ index }">
                    <span>{{ index + 1 }}</span>
                  </template>

                  <template v-slot:item.select="{ item }">
                    <v-checkbox
                      v-model="item.selected"
                      hide-details
                      dense
                    ></v-checkbox>
                  </template>

                  <template v-slot:item.AvailableQty="{ item }">
                    <span>{{ parseFloat(item.AvailableQty || 0).toLocaleString() }}</span>
                  </template>

                  <template v-slot:item.TransferQty="{ item }">
                    <v-text-field
                      v-model="item.TransferQty"
                      type="number"
                      dense
                      outlined
                      hide-details
                      :disabled="!item.selected"
                      placeholder="0"
                    ></v-text-field>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Action Buttons -->
          <v-col cols="12" class="mt-4" v-if="selectedProductInfo.ProductName">
            <v-btn
              color="primary"
              @click="save"
              :loading="loader"
              :disabled="!hasValidTransfers || isSubmitting"
            >
              <v-icon left>mdi-content-save</v-icon>Create Transfer Drafts
            </v-btn>
            <v-btn text @click="reset" class="float-right">
              <v-icon left>mdi-refresh</v-icon>Reset
            </v-btn>
          </v-col>
        </v-row>

        <snackbar ref="snackbar"></snackbar>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
export default {
  computed: {
    activeStore() {
      return localStorage.getItem("store_id");
    },
    userId() {
      return localStorage.getItem("userId");
    },
    role() {
      return localStorage.getItem("userRole");
    },
    hasValidTransfers() {
      return this.storesList.some(
        store => store.selected && parseFloat(store.TransferQty || 0) > 0
      );
    }
  },
  data() {
    return {
      fromStore: {
        id: null,
        Name: null
      },
      smsRef: null,
      selectedProduct: null,
      searchingProduct: false,
      searchProducts: null,
      products: [],
      isLoading: false,
      loader: false,
      isSubmitting: false,
      lastSubmitTime: 0,
      dateReadonly: "N",
      date: new Date().toISOString().substr(0, 10),
      pageItems: 500,
      selectedProductInfo: {
        ProductName: null,
        ItemCode: null,
        productId: null,
        AvailableQty: 0,
        ManualStatus: "N",
        UomEntry: null,
        uomEntryName: "",
        SellingPrice: 0,
        BuyingPrice: 0,
        ugp1: []
      },
      storesList: [],
      storeHeaders: [
        { text: "NO:", value: "index" },
        { text: "Select", value: "select" },
        { text: "Store Name", value: "Name" },
        { text: "Available Qty", value: "AvailableQty" },
        { text: "Transfer Qty", value: "TransferQty" }
      ]
    };
  },
  watch: {
    searchProducts(val) {
      if (val && val !== this.selectedProduct && this.querySelections(val)) {
        this.querySelections(val);
      }
    }
  },
  methods: {
    querySelections(searchText) {
      const self = this;
      const url = "/products/search";
      const data = {
        searchText: searchText
      };
      this.$store
        .dispatch("post", { url, data })
        .then(res => {
          if (res.ResultCode == 1200) {
            if (res.ResponseData.length > 0) {
              self.products = res.ResponseData;
            }
          } else {
            this.$refs.snackbar.show(res.ResultDesc, "red");
          }
        })
        .catch(err => {
          this.$refs.snackbar.show(err, "red");
        });
    },
    setDate(date) {
      this.date = date;
    },
    fetchProductInventory(productId) {
      if (!productId) return;

      if (!this.fromStore || !this.fromStore.id) {
        this.$refs.snackbar.show("Source store not loaded. Please refresh the page.", "red");
        return;
      }

      const self = this;
      this.loader = true;

      // Fetch product details from source store
      this.$store
        .dispatch("get", `/product-inventory/${productId}/${this.fromStore.id}`)
        .then(res => {
          if (res.ResultCode == 1200) {
            const product = res.ResponseData;

            self.selectedProductInfo.productId = productId;
            self.selectedProductInfo.ItemCode = product.ItemCode;
            self.selectedProductInfo.ProductName = product.Name;
            self.selectedProductInfo.AvailableQty = product.AvailableQty;
            self.selectedProductInfo.ManualStatus = product.HasUom == 0 ? "Y" : "N";

            if (product.HasUom === 0) {
              self.selectedProductInfo.BuyingPrice = product.buyingPrice;
              self.selectedProductInfo.SellingPrice = product.sellingPrice;
              self.selectedProductInfo.uomEntryName = "Pc(s)";
              self.selectedProductInfo.UomEntry = 0;
            }

            if (product.HasUom === 1) {
              let ugp1Data = product.ugp1;
              const ugp1Record = ugp1Data.find(record => {
                return record.UomEntry === product.SUoMEntry;
              });
              self.selectedProductInfo.ugp1 = product.ugp1;
              self.selectedProductInfo.UomEntry = product.SUoMEntry;
              self.selectedProductInfo.uomEntryName = ugp1Record.UomName;
              self.selectedProductInfo.BuyingPrice = ugp1Record.BuyingPrice;
              self.selectedProductInfo.SellingPrice = ugp1Record.SellingPrice;
            }

            // Fetch all stores and their inventory for this product
            self.fetchStoresInventory(productId);
          } else {
            self.loader = false;
            this.$refs.snackbar.show(res.ResultDesc, "red");
          }
        })
        .catch(err => {
          self.loader = false;
          this.$refs.snackbar.show(err, "red");
        });
    },
    fetchStoresInventory(productId) {
      const self = this;
      this.$store
        .dispatch("get", `/stores`)
        .then(res => {
          if (res.ResultCode == 1200) {
            // Filter out the source store and stores with invalid IDs
            const stores = res.ResponseData.filter(
              store => store.id && store.id != self.fromStore.id
            );

            // Fetch inventory for each store
            const inventoryPromises = stores.map(store => {
              // Ensure store.id is valid before making API call
              if (!store.id) {
                return Promise.resolve({
                  ...store,
                  AvailableQty: 0,
                  TransferQty: null,
                  selected: false
                });
              }

              return self.$store
                .dispatch("get", `/product-inventory/${productId}/${store.id}`)
                .then(invRes => {
                  if (invRes.ResultCode == 1200) {
                    return {
                      ...store,
                      AvailableQty: invRes.ResponseData.AvailableQty || 0,
                      TransferQty: null,
                      selected: false
                    };
                  }
                  return {
                    ...store,
                    AvailableQty: 0,
                    TransferQty: null,
                    selected: false
                  };
                })
                .catch(() => {
                  return {
                    ...store,
                    AvailableQty: 0,
                    TransferQty: null,
                    selected: false
                  };
                });
            });

            Promise.all(inventoryPromises).then(storesWithInventory => {
              self.storesList = storesWithInventory;
              self.loader = false;
            });
          } else {
            self.loader = false;
            this.$refs.snackbar.show(res.ResultDesc, "red");
          }
        })
        .catch(err => {
          self.loader = false;
          this.$refs.snackbar.show(err, "red");
        });
    },
    uomChanged() {
      const self = this;
      const data = this.selectedProductInfo;

      let uomEntryName = "";
      let ugp1 = data.ugp1;
      if (data.ManualStatus == "N") {
        const ugp1Record = ugp1.find(record => {
          return record.UomId === data.UomEntry;
        });
        uomEntryName = ugp1Record.UomName;
        self.selectedProductInfo.BuyingPrice = ugp1Record.BuyingPrice;
        self.selectedProductInfo.SellingPrice = ugp1Record.SellingPrice;
        self.selectedProductInfo.uomEntryName = uomEntryName;
      }
    },
    save() {
      // Debounce: Prevent double submission within 3 seconds
      const now = Date.now();
      if (this.isSubmitting || (now - this.lastSubmitTime) < 3000) {
        this.$refs.snackbar.show("Please wait, submitting...", "orange");
        return;
      }

      if (!this.selectedProductInfo.productId) {
        this.$refs.snackbar.show("Please select a product!", "red");
        return;
      }

      if (this.date == null || this.date == undefined) {
        this.$refs.snackbar.show("Please select date!", "red");
        return;
      }

      const selectedStores = this.storesList.filter(
        store => store.selected && parseFloat(store.TransferQty || 0) > 0
      );

      if (selectedStores.length === 0) {
        this.$refs.snackbar.show(
          "Please select at least one store and enter transfer quantity!",
          "red"
        );
        return;
      }

      // Set submitting flag
      this.isSubmitting = true;
      this.lastSubmitTime = now;

      // Validate against available quantity
      const totalTransferQty = selectedStores.reduce(
        (sum, store) => sum + parseFloat(store.TransferQty || 0),
        0
      );

      // Get converted qty to validate
      const self = this;
      const details = {
        productId: parseInt(this.selectedProductInfo.productId),
        quantity: totalTransferQty,
        uomEntry: parseInt(this.selectedProductInfo.UomEntry) || 0
      };

      const url = `/converted-qty`;
      this.$store
        .dispatch("post", { url, data: details })
        .then(res => {
          if (res.ResultCode == 1200) {
            const convertedTotalQty = res.ResponseData;

            if (convertedTotalQty > self.selectedProductInfo.AvailableQty) {
              self.isSubmitting = false;
              self.$refs.snackbar.show(
                `Insufficient stock! Available: ${self.selectedProductInfo.AvailableQty}, Requested: ${convertedTotalQty}`,
                "red"
              );
              return;
            }

            // Prepare data for backend
            const transferData = {
              productId: parseInt(self.selectedProductInfo.productId),
              fromStoreId: parseInt(self.fromStore.id),
              docDate: self.date,
              uomEntry: parseInt(self.selectedProductInfo.UomEntry) || 0,
              buyingPrice: parseFloat(self.selectedProductInfo.BuyingPrice),
              sellingPrice: parseFloat(self.selectedProductInfo.SellingPrice),
              itemCode: self.selectedProductInfo.ItemCode,
              productName: self.selectedProductInfo.ProductName,
              stores: selectedStores.map(store => ({
                toStoreId: parseInt(store.id),
                quantity: parseFloat(store.TransferQty)
              }))
            };

            // Post to backend
            self.loader = true;
            const postUrl = `/stock-transfer-per-product`;
            self.$store
              .dispatch("post", { url: postUrl, data: transferData })
              .then(postRes => {
                if (postRes.ResultCode == 1200) {
                  self.$refs.snackbar.show(
                    "Transfer drafts created successfully!",
                    "green"
                  );
                  // Reset form and stay on the page
                  self.loader = false;
                  self.isSubmitting = false;
                  self.reset();
                } else {
                  self.loader = false;
                  self.isSubmitting = false;
                  self.$refs.snackbar.show(postRes.ResultDesc, "red");
                }
              })
              .catch(err => {
                self.loader = false;
                self.isSubmitting = false;
                self.$refs.snackbar.show(err, "red");
              });
          } else {
            self.isSubmitting = false;
            self.$refs.snackbar.show(res.ResultDesc, "red");
          }
        })
        .catch(err => {
          self.isSubmitting = false;
          self.$refs.snackbar.show(err, "red");
        });
    },
    reset() {
      this.selectedProduct = null;
      this.selectedProductInfo = {
        ProductName: null,
        ItemCode: null,
        productId: null,
        AvailableQty: 0,
        ManualStatus: "N",
        UomEntry: null,
        uomEntryName: "",
        SellingPrice: 0,
        BuyingPrice: 0,
        ugp1: []
      };
      this.storesList = [];
      this.products = [];
      this.isSubmitting = false;
      this.lastSubmitTime = 0;
    },
    loadFromStore() {
      const self = this;
      const storeId = this.activeStore;

      if (!storeId) {
        this.$refs.snackbar.show("No active store found", "red");
        return;
      }

      // Fetch store details
      this.$store
        .dispatch("get", `/stores/${storeId}`)
        .then(res => {
          if (res.ResultCode == 1200) {
            self.fromStore = res.ResponseData;
          } else {
            this.$refs.snackbar.show(res.ResultDesc, "red");
          }
        })
        .catch(err => {
          this.$refs.snackbar.show(err, "red");
        });
    },
    getGenSettings() {
      const self = this;
      this.$store
        .dispatch("get", `/general-settings`)
        .then(res => {
          if (res.ResultCode == 1200) {
            self.dateReadonly = res.ResponseData.dateReadonly;
            self.smsRef = res.ResponseData.smsRef;
          } else {
            this.$refs.snackbar.show(res.ResultDesc, "red");
          }
        })
        .catch(err => {
          this.$refs.snackbar.show(err, "red");
        });
    }
  },
  created() {
    // Set fromStore ID using activeStore immediately to prevent "undefined" errors
    this.fromStore.id = parseInt(this.activeStore);

    // Load store name and general settings
    this.loadFromStore();
    this.getGenSettings();
  }
};
</script>

<style lang="scss" scoped>
.custom-accent {
  color: #1976d2;
}
.style-18 {
  font-size: 18px;
  font-weight: bold;
}
</style>
